import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
    fetchNotificationsCsv,
    postNotificationsBulkChangeSendType,
    postNotificationsBulkDelete,
    postNotificationsBulkSend,
} from "../../../../../utils/apiUtils";
import { formatCustomDate } from "../../../../../utils/downloadUtils";
import { NOTIFICATIONS_PAGE_ERROR_TOAST_OPTIONS } from "../constants";

/**
 * TanStack mutations for bulk queue actions and filtered CSV download on the notifications queue footer.
 * Confirmation copy and disabled-state flags stay in {@link BottomBar}.
 *
 * @param {object} params
 * @param {string} params.sendType Active send-type tab / API filter.
 * @param {string | null} params.queueFilter Segment filter (`all` | `sent` | `errors` or null for all).
 * @param {Record<string, string>} params.columnFilters Non-empty column filters for bulk APIs.
 * @param {() => void} params.refreshNotificationsList Refetches list + count for current filters.
 */
export function useBulkNotificationActions({
    sendType,
    queueFilter,
    columnFilters,
    refreshNotificationsList,
}) {
    const downloadCsvMutation = useMutation({
        mutationFn: async () => {
            const res = await fetchNotificationsCsv({
                sendType,
                queueFilter,
                columnFilters,
            });

            const blob = await res.blob();

            const fileName = `notifications-${formatCustomDate(new Date())}.csv`;

            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = fileName;

            a.dispatchEvent(
                new MouseEvent("click", {
                    view: window,
                    bubbles: true,
                    cancelable: true,
                }),
            );

            URL.revokeObjectURL(url);
        },
        onError: () => {
            toast.error(
                "Error generating CSV file",
                NOTIFICATIONS_PAGE_ERROR_TOAST_OPTIONS,
            );
        },
    });

    const bulkMoveMutation = useMutation({
        mutationFn: ({ targetSendType }) =>
            postNotificationsBulkChangeSendType({
                sendType,
                queueFilter,
                columnFilters,
                targetSendType,
            }),
        onSuccess: () => {
            refreshNotificationsList();
        },
        onError: (error) => {
            if (error.response?.status === 504) {
                toast.info(
                    "The operation is running in the background and may take a few minutes to complete.",
                    NOTIFICATIONS_PAGE_ERROR_TOAST_OPTIONS,
                );
            } else {
                toast.error(
                    "Could not change send type for all filtered notifications.",
                    NOTIFICATIONS_PAGE_ERROR_TOAST_OPTIONS,
                );
            }
        },
    });

    const bulkSendMutation = useMutation({
        mutationFn: () =>
            postNotificationsBulkSend({
                sendType,
                queueFilter,
                columnFilters,
            }),
        onSuccess: () => {
            refreshNotificationsList();
        },
        onError: (error) => {
            if (error.response?.status === 504) {
                toast.info(
                    "The operation is running in the background and may take a few minutes to complete.",
                    NOTIFICATIONS_PAGE_ERROR_TOAST_OPTIONS,
                );
            } else {
                toast.error(
                    "Could not send all filtered notifications.",
                    NOTIFICATIONS_PAGE_ERROR_TOAST_OPTIONS,
                );
            }
        },
    });

    const bulkDeleteMutation = useMutation({
        mutationFn: () =>
            postNotificationsBulkDelete({
                sendType,
                queueFilter,
                columnFilters,
            }),
        onSuccess: () => {
            refreshNotificationsList();
        },
        onError: (error) => {
            if (error.response?.status === 504) {
                toast.info(
                    "The operation is running in the background and may take a few minutes to complete.",
                    NOTIFICATIONS_PAGE_ERROR_TOAST_OPTIONS,
                );
            } else {
                toast.error(
                    "Could not delete all filtered notifications.",
                    NOTIFICATIONS_PAGE_ERROR_TOAST_OPTIONS,
                );
            }
        },
    });

    return {
        downloadCsvMutation,
        bulkMoveMutation,
        bulkSendMutation,
        bulkDeleteMutation,
    };
}
